import { useEffect, useState } from 'react';
import { api, fmt, fmtDate } from '../api.ts';

/*
 * Send a job to production without (all of) its board (066).
 *
 * For each board the card is short of, say how it is covered:
 *   - take it from board held for another waiting job (it is lent, and that
 *     job reads short until it is given back),
 *   - use a different board in stock (the card then says what was used), or
 *   - board not here yet (issued when it comes).
 * A reason is always asked for. Everything is recorded and stays highlighted
 * until it is settled.
 */

interface Lender {
  job_id: number; job_number: string; customer: string; held: string;
  delivery_date: string | null; staged: boolean;
}
interface Substitute { item_id: number; name: string; free: string }
interface ShortBoard {
  item_id: number; item_name: string; still_needed: string; available: string; short: string;
  lenders: Lender[]; substitutes: Substitute[];
  on_order: { sheets: string; due: string | null; supplier: string | null } | null;
}
// One tap fills the reason; it can still be edited.
const QUICK_REASONS = ['Customer urgent', 'Board delayed', 'Machine free now'];

type Cover = { cover: '' | 'LOAN' | 'SUBSTITUTE' | 'PENDING'; fromJobId: string; substituteItemId: string; sheets: string };

export default function ReleaseShortDialog({ jobId, jobNumber, onClose, onDone }: {
  jobId: number; jobNumber: string; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [boards, setBoards] = useState<ShortBoard[] | null>(null);
  const [covers, setCovers] = useState<Record<number, Cover>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ boards: ShortBoard[] }>(`/api/pre-press/jobs/${jobId}/release-short`)
      .then((r) => {
        setBoards(r.boards);
        const start: Record<number, Cover> = {};
        for (const b of r.boards) {
          start[b.item_id] = {
            cover: '', fromJobId: b.lenders[0] ? String(b.lenders[0].job_id) : '',
            substituteItemId: b.substitutes[0] ? String(b.substitutes[0].item_id) : '',
            sheets: String(Number(b.still_needed)),
          };
        }
        setCovers(start);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not read what is short'));
  }, [jobId]);

  const set = (itemId: number, p: Partial<Cover>) =>
    setCovers((c) => ({ ...c, [itemId]: { ...c[itemId]!, ...p } }));

  const problem = !boards ? '' : boards.length === 0 ? 'Nothing is short any more — use Move to production'
    : boards.some((b) => !covers[b.item_id]?.cover) ? 'Choose how each board is covered'
    : boards.some((b) => covers[b.item_id]!.cover === 'LOAN' && !covers[b.item_id]!.fromJobId) ? 'Choose the job to take it from'
    : boards.some((b) => {
        const c = covers[b.item_id]!;
        return c.cover === 'SUBSTITUTE' && (!c.substituteItemId || !(Number(c.sheets) > 0) || !Number.isInteger(Number(c.sheets)));
      }) ? 'Choose the board used instead and its sheets'
    : !reason.trim() ? 'reason' : '';

  async function submit() {
    if (problem || !boards) return;
    setBusy(true); setError('');
    try {
      await api.post(`/api/pre-press/jobs/${jobId}/release-short`, {
        reason: reason.trim(),
        boards: boards.map((b) => {
          const c = covers[b.item_id]!;
          return c.cover === 'LOAN' ? { itemId: b.item_id, cover: 'LOAN', fromJobId: Number(c.fromJobId) }
            : c.cover === 'SUBSTITUTE'
              ? { itemId: b.item_id, cover: 'SUBSTITUTE', substituteItemId: Number(c.substituteItemId), sheets: Number(c.sheets) }
              : { itemId: b.item_id, cover: 'PENDING' };
        }),
      });
      const how = boards.map((b) => {
        const c = covers[b.item_id]!;
        const lender = b.lenders.find((l) => String(l.job_id) === c.fromJobId);
        return c.cover === 'LOAN' ? `board from ${lender?.job_number ?? 'another job'}`
          : c.cover === 'SUBSTITUTE' ? `${b.substitutes.find((s) => String(s.item_id) === c.substituteItemId)?.name ?? 'another board'} used instead`
          : `${fmt(b.short)} sheets pending`;
      }).join(' · ');
      onDone(`${jobNumber} sent to production without its board · ${how}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send it to production');
    } finally { setBusy(false); }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Send {jobNumber} without its board</h3>
          <p>For when it has to go now. It stays marked until the board is settled.</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {!boards ? <div className="dim">Loading…</div> : boards.map((b) => {
            const c = covers[b.item_id]!;
            const lender = b.lenders.find((l) => String(l.job_id) === c.fromJobId);
            const free = Number(b.available);
            const shortBy = Number(b.short);
            const taken = lender ? Math.min(shortBy, Number(lender.held)) : 0;
            return (
              <div key={b.item_id} className="field">
                <label>{b.item_name}</label>
                <div className="dim" style={{ fontSize: 13, marginBottom: 8 }}>
                  Needs {fmt(b.still_needed)} · {fmt(b.available)} available · <strong>short {fmt(b.short)}</strong>
                </div>
                <div className="order-list" style={{ marginBottom: 0 }}>
                  <label className="order-row" style={{ cursor: b.lenders.length ? 'pointer' : 'default', alignItems: 'flex-start' }}>
                    <input type="radio" name={`cover-${b.item_id}`} disabled={!b.lenders.length}
                           checked={c.cover === 'LOAN'} onChange={() => set(b.item_id, { cover: 'LOAN' })} />
                    <span style={{ flex: 1 }}>
                      <strong>Take it from another job's board</strong>
                      <div className="dim" style={{ fontSize: 12.5 }}>
                        {b.lenders.length ? 'That job is short until it is given back.' : 'No other job has this board held for it.'}
                      </div>
                      {c.cover === 'LOAN' && lender && (
                        <>
                          <select style={{ marginTop: 6, width: '100%' }} value={c.fromJobId}
                                  onChange={(e) => set(b.item_id, { fromJobId: e.target.value })}>
                            {b.lenders.map((l) => (
                              <option key={l.job_id} value={l.job_id}>
                                {l.job_number} · {l.customer} · {fmt(l.held)} held
                                {l.delivery_date ? ` · due ${fmtDate(String(l.delivery_date).slice(0, 10))}` : ''}
                                {l.staged ? ' · staged' : ''}
                              </option>
                            ))}
                          </select>
                          <div style={{ fontSize: 12.5, marginTop: 6, color: 'var(--ink-2)' }}>
                            {free > 0 ? `${fmt(free)} free + ` : ''}{fmt(taken)} from {lender.job_number}
                            {taken < shortBy ? ` · ${fmt(shortBy - taken)} to come` : ''}
                            {' · '}{lender.job_number} then short {fmt(taken)}
                          </div>
                        </>
                      )}
                    </span>
                  </label>
                  <label className="order-row" style={{ cursor: b.substitutes.length ? 'pointer' : 'default', alignItems: 'flex-start' }}>
                    <input type="radio" name={`cover-${b.item_id}`} disabled={!b.substitutes.length}
                           checked={c.cover === 'SUBSTITUTE'} onChange={() => set(b.item_id, { cover: 'SUBSTITUTE' })} />
                    <span style={{ flex: 1 }}>
                      <strong>Use a different board in stock</strong>
                      <div className="dim" style={{ fontSize: 12.5 }}>
                        {b.substitutes.length ? 'The card is changed to the board actually used.' : 'No other board has free sheets.'}
                      </div>
                      {c.cover === 'SUBSTITUTE' && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <select style={{ flex: 1 }} value={c.substituteItemId}
                                  onChange={(e) => set(b.item_id, { substituteItemId: e.target.value })}>
                            {b.substitutes.map((s) => (
                              <option key={s.item_id} value={s.item_id}>{s.name} · {fmt(s.free)} free</option>
                            ))}
                          </select>
                          <input type="number" min="1" step="1" aria-label="Sheets of that board" style={{ width: 110 }}
                                 value={c.sheets} onChange={(e) => set(b.item_id, { sheets: e.target.value })} />
                        </div>
                      )}
                    </span>
                  </label>
                  <label className="order-row" style={{ cursor: 'pointer', alignItems: 'flex-start' }}>
                    <input type="radio" name={`cover-${b.item_id}`}
                           checked={c.cover === 'PENDING'} onChange={() => set(b.item_id, { cover: 'PENDING' })} />
                    <span style={{ flex: 1 }}>
                      <strong>Board not here yet</strong>
                      <div className="dim" style={{ fontSize: 12.5 }}>Its board is issued when it arrives.</div>
                      {c.cover === 'PENDING' && (
                        <div style={{ fontSize: 12.5, marginTop: 6, color: 'var(--ink-2)' }}>
                          {free > 0 ? `${fmt(free)} go now · ` : ''}{fmt(shortBy)} to come
                          <div style={{ marginTop: 2, color: b.on_order ? undefined : 'var(--soon)' }}>
                            {b.on_order
                              ? `On order: ${fmt(b.on_order.sheets)}${b.on_order.due ? ` · due ${fmtDate(b.on_order.due)}` : ''}${b.on_order.supplier ? ` · ${b.on_order.supplier}` : ''}`
                              : 'Not ordered yet'}
                          </div>
                        </div>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="rs-reason" className="req">Why it is going without its board</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {QUICK_REASONS.map((r) => (
                <button key={r} type="button" className={`chip${reason === r ? ' on' : ''}`}
                        onClick={() => setReason(r)}>{r}</button>
              ))}
            </div>
            <input id="rs-reason" type="text" value={reason} placeholder="e.g. Customer needs it Friday"
                   onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-foot">
          {problem && problem !== 'reason' && <span className="hint" style={{ marginRight: 'auto' }}>{problem}</span>}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || !!problem || !boards}>
            {busy ? 'Sending…' : 'Send to production'}
          </button>
        </div>
      </div>
    </div>
  );
}
